import { useCallback, useEffect, useRef } from 'react';
import { PolymerAxios } from '../utils/axios';
import { API_EndorseForm } from '../utils/api';
import { retry } from '../utils/retry';

// Define a generic interface for form objects
export interface FormDataWithStatus {
  id: number;
  signify_status: string;
  signing_require: boolean;
}

interface UseFormUpdaterProps<T extends FormDataWithStatus> {
  loginUser: string;
  singpassToken: string;
  applicationUuid?: string | null | undefined;
  isUninitialized?: boolean;
  data: T[];
  onRefetch: () => void;
  apiEndpoint?: string;
  retryAttempts?: number;
  retryDelay?: number;
  callBackFn?: () => any;
}

export function useFormUpdater<T extends FormDataWithStatus>({
  loginUser,
  singpassToken,
  applicationUuid,
  isUninitialized,
  data,
  onRefetch,
  apiEndpoint = API_EndorseForm,
  retryAttempts = 1,
  retryDelay = 500,
}: UseFormUpdaterProps<T>) {
  const initialLoadDone = useRef(false);
  const isMounted = useRef(true);
  
  // Call API to trigger signify with given formId
  const triggerBackendUpdateViaProxy = useCallback(
    async (formId: number): Promise<boolean> => {
      try {
        const response = await PolymerAxios(loginUser ?? '', singpassToken).post(`${apiEndpoint}?formId=${formId}`, {
          applicationUuid: applicationUuid,
        });

        // Check for a successful response
        if (response.status === 200) {
          console.log(`[UpdateForm] Successfully updated form ID: ${formId}`);
          return true;
        } else {
          console.error(`[UpdateForm] Unexpected response status for form ID ${formId}: ${response.status}`);
          throw new Error(`Unexpected response status: ${response.status}`);
        }
      } catch (error) {
        console.error(`[UpdateForm] Error updating form ID ${formId}: ${error}`);
        throw error; // Re-throw to trigger retry
      }
    },
    [loginUser, singpassToken, applicationUuid, apiEndpoint]
  );

  // Add function to handle form updates
  const handleFormUpdates = useCallback(() => {
    if (isUninitialized || !data) return;
    // Skip processing if no forms need updates
    const formsToUpdate = data.filter((form) => (form.signify_status.toLowerCase() !== 'signed' && form.signing_require === true));
    if (formsToUpdate.length === 0) return;
    // Process only the forms that are not signed
    const fetchPromises = formsToUpdate.map((form) => {
      return retry(() => triggerBackendUpdateViaProxy(form.id), retryAttempts, retryDelay, `Form-${form.id}`);
    });
    // Wait for all update calls to settle, then refetch data
    Promise.allSettled(fetchPromises).then((results) => {
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      console.log(`[UpdateForm] Updates completed - Success: ${succeeded}, Failed: ${failed}`);
      
      // Only refetch if component is still mounted
      if (isMounted.current) {
        onRefetch();
      } else {
        console.log('[UpdateForm] Component unmounted, skipping refetch');
      }
    });
  }, [isUninitialized, data, triggerBackendUpdateViaProxy, onRefetch, retryAttempts, retryDelay]);

  // Combined initial load and focus effect
  useEffect(() => {
    // Reset mounted ref on component mount
    isMounted.current = true;
    
    // Handle initial load
    if (!initialLoadDone.current && !isUninitialized && data) {
      handleFormUpdates();
      initialLoadDone.current = true;
    }
    // Setup focus handler
    const handleFocus = () => {
      if (!isUninitialized) {
        console.log('[UpdateForm] Window focus detected, checking for updates');
        handleFormUpdates();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      // Set mounted ref to false on unmount
      isMounted.current = false;
      window.removeEventListener('focus', handleFocus);
    };
  }, [isUninitialized, handleFormUpdates, data]);

  return {
    handleFormUpdates,
    triggerBackendUpdateViaProxy,
  };
}
